import { z } from "zod";

// Los inputs numéricos llegan de react-hook-form como string ("" cuando el
// usuario no los toca, ya que ningún <input type="number"> usa valueAsNumber).
// z.coerce.number() convierte "" en 0 (Number("") === 0), lo que grabaría
// silenciosamente un 0 en cada entrada nueva que deja "Orden" vacío. Estos
// cuatro campos son opcionales a propósito (SPEC-011): un "" debe tratarse
// como ausente, no como 0, para que el servidor conserve su propio criterio
// cuando el campo no se especifica.
const optionalInt = <T extends z.ZodTypeAny>(schema: T) =>
  z.preprocess(
    (value) => (value === "" ? undefined : value),
    schema.optional(),
  );

// Mismo criterio que `isEmail` de `api/src/management/catalogs/catalog.service.ts`
// (RFC simplificada, sin librería): el formulario rechaza antes de mandar el
// POST/PUT, en vez de esperar el 400 de la API (caso real, trace
// f4575316-5c75-4c27-83d0-340db0aafd9f: un taller creado con `noticeEmails:
// ["test"]`).
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
// Mismo criterio que `text`/`hex` de `validateScalarAttributes`
// (`catalog.service.ts`): el servidor solo exige texto, pero un color mal
// tipeado (`"rojo"`, `"E30613"` sin `#`) igual llega como 400 hoy — el
// formulario adelanta el formato hexadecimal de 6 dígitos que "Color"
// realmente espera.
const HEX_COLOR_PATTERN = /^#[0-9a-fA-F]{6}$/;

export const catalogEntrySchema = z.object({
  code: z.string().trim().min(1, "El código es obligatorio"),
  nameEs: z.string().trim().min(1, "El nombre es obligatorio"),
  nameEn: z.string(),
  descriptionEs: z.string(),
  descriptionEn: z.string(),
  active: z.boolean(),
  // smallint (`isSmallint`, catalog.service.ts): -32768..32767.
  sortOrder: optionalInt(
    z.coerce
      .number()
      .int()
      .min(-32768, "El orden debe estar entre -32768 y 32767")
      .max(32767, "El orden debe estar entre -32768 y 32767"),
  ),
  color: z.string(),
  isFinal: z.boolean(),
  version: z.string(),
  photoUrl: z.string(),
  hex: z
    .string()
    .refine(
      (value) => value === "" || HEX_COLOR_PATTERN.test(value),
      "El color debe ser un código hexadecimal de 6 dígitos (ej. #E30613)",
    ),
  // unsigned int32 (`isUnsignedInt32`, catalog.service.ts).
  referenceKm: optionalInt(
    z.coerce
      .number()
      .int()
      .min(0, "El kilometraje debe ser un entero positivo")
      .max(2_147_483_647, "El kilometraje debe ser un entero positivo"),
  ),
  // unsigned smallint (`isUnsignedSmallint`, catalog.service.ts).
  durationMin: optionalInt(
    z.coerce
      .number()
      .int()
      .min(0, "La duración debe ser un entero positivo")
      .max(32767, "La duración debe ser un entero positivo"),
  ),
  cityId: z.string(),
  address: z.string(),
  phone: z.string(),
  // unsigned smallint (`isUnsignedSmallint`, catalog.service.ts).
  bays: optionalInt(
    z.coerce
      .number()
      .int()
      .min(0, "El número de bahías debe ser un entero positivo")
      .max(32767, "El número de bahías debe ser un entero positivo"),
  ),
  noticeEmails: z.string().superRefine((value, ctx) => {
    const emails = value
      .split(",")
      .map((item) => item.trim())
      .filter(Boolean);
    const bad = emails.find((email) => !EMAIL_PATTERN.test(email));
    if (bad !== undefined)
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: `Correo de aviso inválido: «${bad}»`,
      });
  }),
  type: z.string(),
  contact: z.string(),
  logoUrl: z.string(),
  // PLAN-007 Fase 4d: id del archivo ya subido y confirmado
  // (`uploadImage`/`FileUploadChip`), con prioridad sobre la URL heredada en
  // el servidor (MerchantInputAttributes.logoFileId / atributo genérico
  // `photoFileId` del catálogo de modelos). Vacío cuando el operador no tocó
  // la imagen en esta sesión del formulario.
  photoFileId: z.string(),
  logoFileId: z.string(),
});
export type CatalogEntryFormValues = z.infer<typeof catalogEntrySchema>;

// Claves reales del formulario (`applyApiFieldErrors`, `errorMessage.ts`):
// un `field` que la API identifica pero que no está acá (`attributes`, un
// atributo desconocido) no tiene input al que asociarse, así que cae al
// toast en vez de perderse en silencio.
export const CATALOG_ENTRY_FORM_FIELDS = Object.keys(
  catalogEntrySchema.shape,
) as readonly (keyof CatalogEntryFormValues)[];
